import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import SidePanel from 'components/application/SidePanel';

import { useProjectCoshhContext } from 'components/contexts/ProjectCoshhContext';
import CoshhForm from 'components/projectCoshh/components/CoshhForm';
import CoshhShow from 'components/projectCoshh/components/CoshhShow';
import FormFooter from 'components/sidepanels/FormFooter';
import ShowFooter from 'components/sidepanels/ShowFooter';

import { focusField } from 'components/helpers/refs';

export default function ProjectCoshhSidePanel({
  coshhDocument,
  isOpen,
  isSubmitDisabled,
  onClose,
  onCoshhInputChange,
  onCoshhOptionChange,
  onDeleteProjectCoshhClick,
  onEditCoshhDocumentSubmit,
  onNewCoshhDocumentSubmit,
  onOptionSelect,
  removeErrorStyling,
  requestError,
  setSidePanelContext,
  sidePanelContext,
}) {
  const sidePanelBodyRef = useRef();
  const sidePanelFieldRef = useRef();

  const { coshhDocumentPolicy } = useProjectCoshhContext();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectCoshh.components.ProjectCoshhSidePanel',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const openEditCoshhDocument = () => {
    setSidePanelContext('edit');
  };

  const onBackdropClick = () => {
    const panelLocked = ['edit', 'new'].includes(sidePanelContext);

    if (!panelLocked) onClose();
  };

  return (
    <SidePanel
      bodyContent={
        sidePanelContext === 'show' ?
          <CoshhShow coshhDocument={coshhDocument} />
        : <CoshhForm
            coshhDocument={coshhDocument}
            onCoshhInputChange={onCoshhInputChange}
            onCoshhOptionChange={onCoshhOptionChange}
            onOptionSelect={onOptionSelect}
            removeErrorStyling={removeErrorStyling}
            requestError={requestError}
            sidePanelFieldRef={sidePanelFieldRef}
          />
      }
      bodyRef={sidePanelBodyRef}
      closeCallback={onClose}
      color='tertiary'
      contentContext={sidePanelContext}
      displayClose={sidePanelContext === 'show'}
      footerContent={
        sidePanelContext === 'show' ?
          <ShowFooter
            deleteText={i18nT('show.footer.deleteText')}
            editText={i18nT('show.footer.editText')}
            isDeleteButtonHidden={!coshhDocumentPolicy.canDestroy}
            isEditButtonHidden={!coshhDocumentPolicy.canUpdate}
            onDeleteClick={() => onDeleteProjectCoshhClick(true)}
            onEditClick={openEditCoshhDocument}
          />
        : sidePanelContext === 'new' && coshhDocumentPolicy.canCreate ?
          <FormFooter
            onCancel={onClose}
            onSubmit={onNewCoshhDocumentSubmit}
            submitButtonDisabled={isSubmitDisabled}
            text={i18nTCommon('projects.coshh.addCoshh')}
          />
        : sidePanelContext === 'edit' &&
          coshhDocumentPolicy.canUpdate && (
            <FormFooter
              onCancel={onClose}
              onSubmit={onEditCoshhDocumentSubmit}
              submitButtonDisabled={isSubmitDisabled}
              text={i18nTCommon('shared.saveChanges')}
            />
          )

      }
      headerContent={
        <div className='popup__title popup__title--tertiary-split'>
          <h1
            className={classNames(
              sidePanelContext === 'show' ?
                'tw-mb-2 tw-text-s tw-font-medium tw-tracking-tight'
              : 'tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900',
            )}
          >
            {sidePanelContext === 'edit' ?
              i18nT('edit.title')
            : sidePanelContext === 'new' ?
              i18nT('new.title')
            : sidePanelContext === 'show' &&
              i18nTCommon('projects.coshh.coshhAssessment')
            }
          </h1>
          <h2 className='tw-mt-2 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-700'>
            {coshhDocument.title}
          </h2>
          {sidePanelContext === 'show' && (
            <>
              {coshhDocument.manufacturer && (
                <p
                  className={classNames(
                    'tw-mt-2 tw-text-l tw-font-normal tw-text-grey-500',
                    coshhDocument.reference && 'tw-mb-2',
                  )}
                >
                  {coshhDocument.manufacturer.toUpperCase()}
                </p>
              )}
              {coshhDocument.reference && (
                <p className='tw-mb-0 tw-mt-2 tw-text-l tw-font-normal tw-text-grey-500'>
                  {i18nT('show.reference', {
                    interpolation: { escapeValue: false },
                    reference: coshhDocument.reference.toUpperCase(),
                  })}
                </p>
              )}
            </>
          )}
        </div>
      }
      isOpen={isOpen}
      onBackdropClick={onBackdropClick}
      onOpen={() => sidePanelContext === 'new' && focusField(sidePanelFieldRef)}
      scrollHeader={false}
      submitDisabled={false}
    />
  );
}

ProjectCoshhSidePanel.propTypes = {
  coshhDocument: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isSubmitDisabled: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCoshhInputChange: PropTypes.func.isRequired,
  onCoshhOptionChange: PropTypes.func.isRequired,
  onDeleteProjectCoshhClick: PropTypes.func,
  onEditCoshhDocumentSubmit: PropTypes.func,
  onNewCoshhDocumentSubmit: PropTypes.func,
  onOptionSelect: PropTypes.func,
  setSidePanelContext: PropTypes.func.isRequired,
  sidePanelContext: PropTypes.string.isRequired,
};
